import { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { getAiAssistantHome } from '../../api/aiAssistant';
import { AiAssistantHeader } from '../../components/business/AiAssistantHeader';
import { AssistantConversationThread } from '../../components/business/AssistantConversationThread';
import { AssistantInputBar } from '../../components/business/AssistantInputBar';
import { AssistantModeBar } from '../../components/business/AssistantModeBar';
import { AssistantWelcomeCard } from '../../components/business/AssistantWelcomeCard';
import { FunFactCard } from '../../components/business/FunFactCard';
import { LearningProfilePromptCard } from '../../components/business/LearningProfilePromptCard';
import { EmptyView } from '../../components/ui/EmptyView';
import { ErrorView } from '../../components/ui/ErrorView';
import { LoadingView } from '../../components/ui/LoadingView';
import { ScreenBackground } from '../../components/ui/ScreenBackground';
import { showAppToast } from '../../components/ui/AppToast';
import { tokens } from '../../theme/tokens';
import type {
  AssistantChatMessageViewModel,
  AiAssistantHomeViewModel,
  AssistantMode,
  AssistantPromptViewModel,
} from '../../types/aiAssistant';

type AiAssistantPageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; assistantHome: AiAssistantHomeViewModel };

const modeLabelMap: Record<AssistantMode, string> = {
  explain: '讲题',
  correct: '批改',
  paper: '试卷',
};

const getInputPlaceholder = (mode: AssistantMode): string => {
  if (mode === 'correct') {
    return '描述作业批改需求，或切到拍照上传';
  }
  if (mode === 'paper') {
    return '输入试卷问题，或进入试卷分析';
  }
  return '输入题目问题或按住说话';
};

const createAssistantReply = (
  question: string,
  mode: AssistantMode,
  idSeed: number
): AssistantChatMessageViewModel => ({
  id: `assistant-${idSeed}`,
  role: 'assistant',
  mode,
  content: `收到，我先按${modeLabelMap[mode]}模式帮你梳理：“${question}”。真实 AI 接口接入前，这里先展示本地对话占位。`,
});

export default function AiAssistantTabPage() {
  const router = useRouter();
  const [state, setState] = useState<AiAssistantPageState>({ status: 'loading' });
  const [activeMode, setActiveMode] = useState<AssistantMode>('explain');
  const [conversationMessages, setConversationMessages] = useState<AssistantChatMessageViewModel[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isRecording, setRecording] = useState(false);

  const loadAssistantHome = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const assistantHome = await getAiAssistantHome();
      if (!assistantHome.greeting && assistantHome.prompts.length === 0) {
        setState({ status: 'empty' });
        return;
      }
      setState({ status: 'success', assistantHome });
    } catch {
      setState({ status: 'error', message: '暂时无法加载 AI 学伴' });
    }
  }, []);

  useEffect(() => {
    void loadAssistantHome();
  }, [loadAssistantHome]);

  const openPhotoSearch = useCallback(() => {
    router.push('/photo-search');
  }, [router]);

  const showDevelopmentToast = useCallback((featureName: string) => {
    showAppToast(`${featureName}功能开发中`, 'info');
  }, []);

  const handleChangeMode = useCallback(
    (mode: AssistantMode) => {
      setActiveMode(mode);
      if (mode === 'paper') {
        router.push('/paper-analysis');
        return;
      }
      router.push('/photo-search');
    },
    [router]
  );

  const handlePressPrompt = useCallback(
    (prompt: AssistantPromptViewModel) => {
      if (prompt.routeKey === 'photo-search') {
        router.push('/photo-search');
      }
    },
    [router]
  );

  const handleSend = useCallback(() => {
    const question = inputValue.trim();
    if (!question) {
      return;
    }

    const idSeed = Date.now();
    setConversationMessages((current) => [
      ...current,
      {
        id: `student-${idSeed}`,
        role: 'student',
        mode: activeMode,
        content: question,
      },
      createAssistantReply(question, activeMode, idSeed + 1),
    ]);
    setInputValue('');
  }, [activeMode, inputValue]);

  if (state.status === 'loading') {
    return (
      <SafeAreaView style={styles.screen}>
        <ScreenBackground style={styles.background}>
          <LoadingView message="正在加载 AI 学伴" />
        </ScreenBackground>
      </SafeAreaView>
    );
  }

  if (state.status === 'error') {
    return (
      <SafeAreaView style={styles.screen}>
        <ScreenBackground style={styles.background}>
          <ErrorView message={state.message} onRetry={loadAssistantHome} />
        </ScreenBackground>
      </SafeAreaView>
    );
  }

  if (state.status === 'empty') {
    return (
      <SafeAreaView style={styles.screen}>
        <ScreenBackground style={styles.background}>
          <EmptyView title="暂无问答内容" description="开始和 AI 学伴对话吧。" />
        </ScreenBackground>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.screen}>
      <ScreenBackground style={styles.background}>
        <ScrollView contentContainerStyle={styles.content}>
          <AiAssistantHeader
            title="AI学伴·峰峰"
            subtitle="讲题、批改和试卷分析都可以找我"
            onPressAvatar={() => showDevelopmentToast('学伴信息卡片')}
            onPressMore={() => router.push('/conversation-history')}
          />
          <AssistantWelcomeCard greeting={state.assistantHome.greeting} />
          <AssistantConversationThread messages={conversationMessages} />
          <LearningProfilePromptCard
            prompts={state.assistantHome.prompts}
            onPressPrompt={handlePressPrompt}
          />
          <FunFactCard
            title={state.assistantHome.funFact.title}
            content={state.assistantHome.funFact.content}
          />
          <View style={styles.spacer} />
        </ScrollView>
        <View style={styles.footer}>
          <AssistantModeBar activeMode={activeMode} onChangeMode={handleChangeMode} />
          <AssistantInputBar
            value={inputValue}
            placeholder={getInputPlaceholder(activeMode)}
            isRecording={isRecording}
            onChangeText={setInputValue}
            onSend={handleSend}
            onStartVoice={() => setRecording(true)}
            onEndVoice={() => setRecording(false)}
          />
        </View>
      </ScreenBackground>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  background: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.md,
  },
  spacer: {
    height: tokens.spacing.xxl,
  },
  footer: {
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.lg,
    borderTopWidth: tokens.border.width,
    borderTopColor: tokens.colors.border,
    backgroundColor: tokens.colors.surface,
  },
});
